import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { PrimeNGHelpersService } from 'ng/src/app/core/services/primeng-helpers.service';
import { LogService } from '../log.service';
import { Log, LogConfig, LogWithLogOwnerUser } from '../log.model';
import { forkJoin, of } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { LogInvitation, LogListRowData, LogListViewModel } from './log-list.model';
import { UserService } from 'ng/src/app/users/shared/user.service';
import { Utils } from '../../core/utils';
import { NoteService } from '../../notes/shared/note.service';
import { ILocationService } from 'angular';
import { CustomErrorProperties } from '../../shared/model/error.model';
import { Projections } from "app/shared/model/request-parameter.model";
import { ConfirmationService, FilterMetadata } from "primeng/api";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { Table } from 'primeng/table';
import { SimulateLogUserRoleService } from "ng/src/app/shared/simulate-log-user-role.service";

@Component({
	templateUrl: './log-list.component.html',
	styleUrls: ['./log-list.component.css']
})
export class LogListComponent implements OnInit, AfterViewInit {
	private readonly nonAdminColumns = {
		description: false,
		lastNoteDateTime: true
	}
	private readonly adminColumns = {
		description: false,
		logOwner: false,
		logStatus: false,
		paymentStatus: false,
		lastNoteDateTime: true,
		paymentExpiresDateTime: false
	}
	private readonly hasAdminColumns = AppStateService.isAdmin || AppStateService.isSwitchedUser;

	camel2Title = Utils.camelToTitleCase;
	columns: { [key: string]: boolean } = {
		name: true,
		...AppStateService.preferences.getValue('log-list-columns',
			this.hasAdminColumns ? this.adminColumns : this.nonAdminColumns)
	};
	filterTable = PrimeNGHelpersService.filterTable;
	loading = true;
	searchFilter = '';
	viewModel = {} as LogListViewModel;
	
	get visibleColKeys() {
		return Object.keys(this.columns).filter(c => this.columns[c]);
	}
	get filterList() {
		return this.visibleColKeys.filter(col => !col.endsWith('DateTime'));
	}

	@ViewChild(Table) table!: Table;

	constructor(
		private logService: LogService,
		private userService: UserService,
		private noteService: NoteService,
		@Inject('$location') private $location: ILocationService,
		private confirmationService: ConfirmationService,
		private simulateLogUserRoleService: SimulateLogUserRoleService
	) { }

	ngOnInit() {
		forkJoin({
			logInvitations: this.logService.getLogInvitations(),
			logs: this.logService.getLogs<LogWithLogOwnerUser[]>(!this.simulateLogUserRoleService.forceAdminFlag, Projections.Logs.withLogOwnerUser),
			logDefaultPages: this.logService.getLogConfigValues('defaultLogPage')
		})
		.pipe(
			tap(r => {
				this.mapToViewModel(r.logs, r.logDefaultPages, r.logInvitations);
			}),
			catchError((error: CustomErrorProperties) => {
				error.sticky = true;

				throw error;
			})
		)
		.subscribe()
		.add(() => this.loading = false);
	}
	
	ngAfterViewInit() {
		this.searchFilter = (this.table.filters.global as FilterMetadata)?.value || '';
	}

	mapToViewModel = (logs: LogWithLogOwnerUser[], defaultLogPages: LogConfig[], logInvitations: LogInvitation[]) => {
		const unverifiedEmails = this.userService.unverifiedEmails;

		if (unverifiedEmails.length) {
			const gt1 = unverifiedEmails.length > 1,
				  emailCSVs = Utils.oxfordCommafy(unverifiedEmails);

			this.viewModel.unverifiedEmails = `${gt1 ? 'Logs have' : 'A log has'} been shared with ${emailCSVs}, but ${gt1 ? 'those addresses have' : 'that address has'} 
				not been verified. Please verify your email ${gt1 ? 'addresses' : 'address'} on the <a href='/#/settings'>user settings page</a>.`
		}

		this.viewModel.defaultLogPages = defaultLogPages;

		this.viewModel.logs = [...logInvitations.map(inv => inv.log), ...logs].map(log => {
			const logInvitationId = logInvitations.find(inv => inv.log.id == log.id)?.id;
			const logOwner = logInvitationId ? null : (<LogWithLogOwnerUser>log).logOwnerUser;

			const rowData = {
				description: log.description,
				lastNoteDateTime: Utils.getDate(log.lastNote?.noteTime),
				logId: log.id,
				name: log.name,
				logInvitationId: logInvitationId,
				paymentExpiresDateTime: Utils.getDate((log as Log).logOwner?.paymentExpires), // undefined for invitations
				paymentStatus: logInvitationId ? null : this.getPaymentStatus(log.millisToPaymentExpiration),
				scheduledForDeletion: log.millisToLogDeletion && log.millisToLogDeletion < 7 * 24 * 60 * 60 * 1000,
				logOwner: logOwner ? `${logOwner?.firstName} ${logOwner?.lastName}` : '',
			} as LogListRowData;

			this.setLogStatus(rowData);

			return rowData;
		});
	}

	selectLog(rowData: LogListRowData) {
		const defaultPage = this.viewModel.defaultLogPages.find(lc => lc.logId == rowData.logId)?.value;

		this.$location.url(defaultPage || '/notes');

		this.logService.selectLog(rowData.logId);
	}

	confirmInvitationResponse(event: Event, rowData: LogListRowData, accepted: boolean) {
		this.confirmationService.confirm({
			target: event.target ?? undefined,
			key: 'invitationConfirmation',
			message: `Are you sure you want to ${accepted ? 'accept' : 'decline'} the invitation for ${rowData.name}?`,
			accept: () => this.respondToInvitation(rowData, accepted)
		});
	}

	respondToInvitation(rowData: LogListRowData, accepted: boolean) {
		let log: Log;

		this.logService.respondToLogInvitation(rowData.logInvitationId!, accepted)
			.pipe(
				tap(r => log = r._embedded.log),
				mergeMap(() => accepted ? this.noteService.getRecentNotes(rowData.logId) : of([]))
			)
			.subscribe(
				notes => {
					if (accepted) {
						rowData.logInvitationId = undefined;
						rowData.lastNoteDateTime = Utils.getDate(notes[0]?.noteTime);
						rowData.paymentExpiresDateTime = Utils.getDate(log.logOwner.paymentExpires);
						rowData.paymentStatus = this.getPaymentStatus(log.millisToPaymentExpiration)

						this.setLogStatus(rowData);

						this.viewModel.logs = [...this.viewModel.logs];
					}
					else
						this.viewModel.logs = this.viewModel.logs.filter(log => log.logId != rowData.logId);
				}
			);
	}

	onCreateLogClicked() {
		this.$location.url('/log-detail')
	}
	
	getStateKey() {
		return `${AppStateService.currentUser?.id}.log-list`;
	}
	
	onColumnsChanged() {
		this.filterTable(this.table, this.searchFilter);

		AppStateService.preferences.setValue('log-list-columns', this.columns);
	}
	isForceLogAdmin() {
		return this.simulateLogUserRoleService.forceAdminFlag;
	}

	private getPaymentStatus(millisToLogBookExpiration: number) {
		return millisToLogBookExpiration <= 0 ? 'Payment Expired' :
			Utils.isBetween(millisToLogBookExpiration, 0, 604800000) ? 'Payment Expires Soon' :
			'Paid';
	}

	private setLogStatus(rowData: LogListRowData) {
		rowData.logStatus =
			rowData.logInvitationId ? 'Pending Invitation' :
			rowData.scheduledForDeletion ? 'Scheduled For Deletion' :
			'Active';
	}
}